import React, { useEffect, useState, useRef } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  Building2,
  Save,
  Lock,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Users,
  DollarSign,
  TrendingUp,
  MapPin,
  Globe,
  Phone,
  Mail,
  ShieldCheck,
  X,
  Send,
  Sparkles,
} from 'lucide-react';

export default function StartupProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving' | 'saved' | 'error' | 'conflict'
  const [errorMessage, setErrorMessage] = useState('');
  const [changeModalField, setChangeModalField] = useState(null);
  const [requestValue, setRequestValue] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [submittingChange, setSubmittingChange] = useState(false);

  // Field refs for quick jump
  const fieldRefs = {
    oneLiner: useRef(null),
    sector: useRef(null),
    website: useRef(null),
    founderPhone: useRef(null),
    businessModel: useRef(null),
    targetMarket: useRef(null),
    city: useRef(null),
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const data = await portalApi.getStartupPortalProfile();
      setProfile(data);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to load startup profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!profile) return;
    try {
      setSaveStatus('saving');
      setErrorMessage('');
      const payload = {
        clientVersion: profile.profileVersion || 1,
        updates: {
          oneLiner: profile.oneLiner,
          sector: profile.sector,
          website: profile.website,
          founderPhone: profile.founderPhone,
          officeAddress: profile.officeAddress,
          socialLinks: profile.socialLinks,
          companyProfile: profile.companyProfile,
          fundingInfo: profile.fundingInfo,
        },
      };

      const res = await portalApi.updateStartupPortalProfile(payload);
      setProfile((prev) => ({
        ...prev,
        profileVersion: res.profileVersion,
        pendingChanges: res.pendingChanges || prev.pendingChanges,
      }));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      if (err.message && err.message.includes('Conflict')) {
        setSaveStatus('conflict');
        setErrorMessage('Another user updated this profile. Please reload to review and merge latest edits.');
      } else {
        setSaveStatus('error');
        setErrorMessage(err.message || 'Failed to save changes');
      }
    }
  };

  const submitCorrectionRequest = async () => {
    if (!changeModalField || !requestValue.trim()) return;
    try {
      setSubmittingChange(true);
      const payload = {
        clientVersion: profile.profileVersion || 1,
        changeRequests: [
          {
            field: changeModalField.key,
            oldValue: changeModalField.currentValue,
            requestedValue: requestValue.trim(),
            reason: requestReason.trim(),
          },
        ],
      };
      const res = await portalApi.updateStartupPortalProfile(payload);
      setProfile((prev) => ({
        ...prev,
        profileVersion: res.profileVersion,
        pendingChanges: res.pendingChanges || prev.pendingChanges,
      }));
      setChangeModalField(null);
      setRequestValue('');
      setRequestReason('');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      alert(err.message || 'Failed to submit correction request');
    } finally {
      setSubmittingChange(false);
    }
  };

  const jumpToFirstMissing = () => {
    if (profile?.firstMissingField) {
      const matchKey = Object.keys(fieldRefs).find((k) =>
        profile.firstMissingField.toLowerCase().includes(k.toLowerCase())
      );
      if (matchKey && fieldRefs[matchKey]?.current) {
        fieldRefs[matchKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs[matchKey].current.focus();
      }
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-white/40 text-sm flex items-center justify-center gap-2">
        <div className="w-4 h-4 border-2 border-[#D42B92] border-t-transparent rounded-full animate-spin" />
        <span>Loading Startup Profile...</span>
      </div>
    );
  }

  const {
    name,
    logo,
    oneLiner,
    sector,
    website,
    stage,
    batch,
    mentors,
    founderName,
    founderEmail,
    founderPhone,
    companyProfile = {},
    fundingInfo = {},
    officeAddress = {},
    socialLinks = {},
    teamMembers = [],
    lockedFields = [],
    healthScore = 0,
    missingFields = [],
    pendingChanges = [],
  } = profile || {};

  return (
    <div className="space-y-8">
      {/* ─── 1. HEADER HERO (PDF Page 5) ─── */}
      <div className="rounded-3xl border border-[#2C2C34] bg-[#131318] p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#D42B92]/10 rounded-full blur-[90px] pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="flex items-start gap-4">
            {logo ? (
              <img src={logo} alt="" className="w-16 h-16 rounded-2xl object-cover border border-[#2C2C34] bg-[#08080A]" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#321129] border border-[#D42B92]/40 flex items-center justify-center text-xl font-bold text-[#FF8EC5]">
                {name?.charAt(0) || 'S'}
              </div>
            )}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-[#F2F2F4] tracking-tight">{name}</h1>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#D42B92]/20 text-[#FF8EC5] border border-[#D42B92]/30 font-medium">
                  {stage || 'Seed'} Stage
                </span>
                {batch?.name && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#08080A] text-[#8E8E99] border border-[#2C2C34]">
                    Cohort: {batch.name}
                  </span>
                )}
              </div>
              <p className="text-sm text-[#8E8E99] max-w-xl">
                {oneLiner || 'Add a compelling one-liner elevator pitch below to summarize your venture.'}
              </p>
            </div>
          </div>

          {/* Profile Health Widget */}
          <div className="bg-[#08080A] border border-[#2C2C34] rounded-2xl p-5 min-w-[280px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#8E8E99] font-medium">Profile Completeness</span>
              <span className="text-base font-bold text-[#FF8EC5]">{healthScore}%</span>
            </div>
            <div className="w-full bg-[#131318] border border-[#2C2C34] rounded-full h-2.5 overflow-hidden mb-2 p-0.5">
              <div
                className="bg-gradient-to-r from-[#D42B92] to-[#FF8EC5] h-full rounded-full transition-all duration-500"
                style={{ width: `${healthScore}%` }}
              />
            </div>
            {missingFields.length > 0 ? (
              <button
                type="button"
                onClick={jumpToFirstMissing}
                className="text-[11px] text-[#FF8EC5] hover:underline flex items-center gap-1"
              >
                <span>Complete missing: {missingFields[0]}</span>
                <Sparkles className="w-3 h-3" />
              </button>
            ) : (
              <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Profile 100% complete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Save Action Feedback */}
      {saveStatus === 'saved' && (
        <div className="p-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          <span>Startup profile changes saved successfully.</span>
        </div>
      )}
      {saveStatus === 'conflict' && (
        <div className="p-3.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>{errorMessage}</span>
          </div>
          <button onClick={loadProfile} className="px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 font-semibold">
            Reload Latest
          </button>
        </div>
      )}

      {/* Pending Change Requests Banner */}
      {pendingChanges.filter((c) => c.status === 'pending').length > 0 && (
        <div className="p-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs space-y-2">
          <div className="font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <span>Pending Verified Field Change Requests</span>
          </div>
          <p className="text-white/60">
            You have {pendingChanges.filter((c) => c.status === 'pending').length} verified field update(s) under review by accelerator ops. Verified fields cannot be overwritten directly.
          </p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* ─── 2. TWO-COLUMN LAYOUT: ESSENTIALS & VERIFIED LEGAL ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Column A: Editable Pitch & Presence */}
          <div className="p-6 rounded-2xl border border-[#2C2C34] bg-[#131318] space-y-4">
            <h2 className="text-base font-semibold text-[#F2F2F4]">Market Presence & Pitch</h2>

            <div>
              <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">One-Liner Tagline (Max 140 chars)</label>
              <input
                ref={fieldRefs.oneLiner}
                type="text"
                maxLength={140}
                value={profile?.oneLiner || ''}
                onChange={(e) => setProfile({ ...profile, oneLiner: e.target.value })}
                placeholder="e.g. Autonomous AI dispatch for cold-chain logistics"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Industry Sector</label>
                <input
                  ref={fieldRefs.sector}
                  type="text"
                  value={profile?.sector || ''}
                  onChange={(e) => setProfile({ ...profile, sector: e.target.value })}
                  placeholder="e.g. Agritech, Climate"
                  className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
                />
              </div>
              <div>
                <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Website URL</label>
                <input
                  ref={fieldRefs.website}
                  type="url"
                  value={profile?.website || ''}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  placeholder="https://company.com"
                  className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Problem Statement</label>
              <textarea
                rows={3}
                value={profile?.companyProfile?.problemStatement || ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    companyProfile: { ...profile.companyProfile, problemStatement: e.target.value },
                  })
                }
                placeholder="What core industry pain point does your venture solve?"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl p-3 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>

            <div>
              <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Business / Revenue Model</label>
              <textarea
                ref={fieldRefs.businessModel}
                rows={3}
                value={profile?.companyProfile?.businessModel || ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    companyProfile: { ...profile.companyProfile, businessModel: e.target.value },
                  })
                }
                placeholder="How does your startup generate revenue? (B2B SaaS, commission, etc.)"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl p-3 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>
          </div>

          {/* Column B: Verified Legal Records (Locked with Request Correction) */}
          <div className="p-6 rounded-2xl border border-[#2C2C34] bg-[#131318] space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-[#F2F2F4] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#D42B92]" />
                <span>Verified Legal Records</span>
              </h2>
              <span className="text-[10px] uppercase font-semibold text-[#8E8E99] tracking-wider">
                Admin Verified Only
              </span>
            </div>

            {/* Legal Name */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[#8E8E99] font-medium">Registered Entity Legal Name</label>
                <button
                  type="button"
                  onClick={() => setChangeModalField({ key: 'name', label: 'Company Legal Name', currentValue: name })}
                  className="text-[11px] text-[#D42B92] hover:underline flex items-center gap-1 font-medium"
                >
                  <Lock className="w-3 h-3" /> Request Correction
                </button>
              </div>
              <input
                type="text"
                readOnly
                value={name || ''}
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4]/70 cursor-not-allowed text-sm"
              />
            </div>

            {/* Registration Number */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[#8E8E99] font-medium">CIN / Registration Number</label>
                <button
                  type="button"
                  onClick={() =>
                    setChangeModalField({
                      key: 'registrationNumber',
                      label: 'Registration Number',
                      currentValue: companyProfile.registrationNumber || '',
                    })
                  }
                  className="text-[11px] text-[#D42B92] hover:underline flex items-center gap-1 font-medium"
                >
                  <Lock className="w-3 h-3" /> Request Correction
                </button>
              </div>
              <input
                type="text"
                readOnly
                value={companyProfile.registrationNumber || 'Not verified'}
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4]/70 cursor-not-allowed text-sm"
              />
            </div>

            {/* Founded Year & Team Size */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-[#8E8E99] font-medium">Founded Year</label>
                  <Lock className="w-3 h-3 text-[#8E8E99]/50" />
                </div>
                <input
                  type="text"
                  readOnly
                  value={companyProfile.foundedYear || 'N/A'}
                  className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4]/70 cursor-not-allowed text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Team Size (FTEs)</label>
                <input
                  type="text"
                  value={companyProfile.teamSize || ''}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      companyProfile: { ...profile.companyProfile, teamSize: e.target.value },
                    })
                  }
                  placeholder="e.g. 8 full-time"
                  className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
                />
              </div>
            </div>

            {/* Office Location */}
            <div>
              <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Office City / Headquarters</label>
              <input
                ref={fieldRefs.city}
                type="text"
                value={officeAddress.city || ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    officeAddress: { ...profile.officeAddress, city: e.target.value },
                  })
                }
                placeholder="e.g. Jaipur, Rajasthan"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3.5 py-2.5 text-[#F2F2F4] text-sm focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>
          </div>
        </div>

        {/* ─── 3. TEAM MEMBERS & ROLES ─── */}
        <div className="p-6 rounded-2xl border border-[#2C2C34] bg-[#131318] space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[#F2F2F4] flex items-center gap-2">
              <Users className="w-4 h-4 text-[#D42B92]" />
              <span>Founders & Team Roster</span>
            </h2>
            <span className="text-xs text-[#8E8E99]">{teamMembers.length + 1} Authorized Seats</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Primary Founder Card */}
            <div className="p-4 rounded-xl border border-[#2C2C34] bg-[#08080A] flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#321129] border border-[#D42B92]/40 flex items-center justify-center text-xs font-bold text-[#D42B92]">
                {founderName?.charAt(0) || 'F'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#F2F2F4] truncate">{founderName || 'Lead Founder'}</div>
                <div className="text-xs text-[#8E8E99] truncate">{founderEmail}</div>
                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  Primary Founder
                </span>
              </div>
            </div>

            {/* Additional Members */}
            {teamMembers.map((member) => (
              <div key={member.id} className="p-4 rounded-xl border border-[#2C2C34] bg-[#08080A] flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#181820] border border-[#2C2C34] flex items-center justify-center text-xs font-bold text-[#8E8E99]">
                  {(member.name || member.email || 'T').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#F2F2F4] truncate">{member.name || member.email || 'Team Member'}</div>
                  <div className="text-xs text-[#8E8E99] truncate">{member.email || ''}</div>
                  <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#181820] text-[#8E8E99] border border-[#2C2C34]">
                    {member.role || 'Team Member'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── 4. BOTTOM SAVE ACTION BAR ─── */}
        <div className="sticky bottom-6 z-30 p-4 rounded-2xl border border-[#2C2C34] bg-[#131318]/95 backdrop-blur-md flex items-center justify-between shadow-2xl">
          <div className="text-xs text-[#8E8E99]">
            Version: <span className="font-mono text-[#F2F2F4]">v{profile?.profileVersion || 1}</span> (Concurrency guarded)
          </div>

          <button
            type="submit"
            disabled={saveStatus === 'saving'}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-semibold flex items-center gap-2 transition-all shadow-lg shadow-[#D42B92]/25 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saveStatus === 'saving' ? 'Saving Profile...' : 'Save Profile Changes'}</span>
          </button>
        </div>
      </form>

      {/* ─── MODAL: REQUEST CORRECTION ON VERIFIED FIELD ─── */}
      {changeModalField && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-[#2C2C34]">
              <h3 className="text-sm font-bold text-[#F2F2F4] flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#D42B92]" />
                <span>Request Correction: {changeModalField.label}</span>
              </h3>
              <button
                onClick={() => setChangeModalField(null)}
                className="p-1 rounded text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-[#8E8E99]">
              This field is verified by program administration. Submitting this request will queue an official review without overwriting verified registry records.
            </p>

            <div>
              <label className="block text-xs text-[#8E8E99] mb-1">Current Verified Value</label>
              <input
                type="text"
                readOnly
                value={changeModalField.currentValue || 'None'}
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3 py-2 text-[#8E8E99] text-xs cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs text-[#F2F2F4] mb-1 font-medium">New Requested Value</label>
              <input
                type="text"
                value={requestValue}
                onChange={(e) => setRequestValue(e.target.value)}
                placeholder="Enter new correct value"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3 py-2 text-[#F2F2F4] text-xs focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>

            <div>
              <label className="block text-xs text-[#F2F2F4] mb-1 font-medium">Reason / Legal Evidence</label>
              <textarea
                rows={3}
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Explain the reason for this legal entity correction..."
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl p-2.5 text-[#F2F2F4] text-xs focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setChangeModalField(null)}
                className="px-3.5 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingChange || !requestValue.trim()}
                onClick={submitCorrectionRequest}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{submittingChange ? 'Submitting...' : 'Submit for Review'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
